import { useFormContext } from 'react-hook-form';
import { startCase } from 'lodash';
import React from 'react';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2_task_CampaignContentTask,
} from 'api/campaignV2/types/GetCampaignOffers';
// Helpers
import { getDateLabelFromDeliverable } from 'helpers/deliverables';
import { AWS_MEDIACONVERT_VIDEO_FORMATS } from 'helpers/videoFormats';
import {
  CampaignMediaType,
  CbDeliverableItemV2Status,
} from 'api/graphql-global-types';
// UI
import { Typography } from 'antd';
// Components
import DropdownItem from '../DropdownItem/DropdownItem';
import UploadTimelineFilesInput from '../UploadTimelineFilesInput/UploadTimelineFilesInput';
// Styles
import styles from './SubmitContent.module.scss';

const { Text } = Typography;

type SubmitContentProps = {
  item?: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2 | null;
  deliverable?: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2 | null;
  formIndex: number;
};

const SubmitContent: React.FC<SubmitContentProps> = ({
  item,
  deliverable,
  formIndex,
}) => {
  const methods = useFormContext();
  const index = item?.task?.orderIdx ?? 0;
  const mediaType = (
    item?.task as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2_task_CampaignContentTask
  )?.mediaType;
  const isImage = mediaType === CampaignMediaType.IMAGE;
  const isRejected = item?.status === CbDeliverableItemV2Status.REJECTED;
  const isApproved = item?.status === CbDeliverableItemV2Status.APPROVED;

  const filesUploaded =
    methods.getValues(`content.${formIndex}.files`)?.length > 0;

  return (
    <div className={styles.root}>
      <div className={styles.titleContainer}>
        <div className={styles.title}>
          <Text>
            Content {index + 1} - {item?.task?.title}
          </Text>
          {isRejected && (
            <span className={styles.rejected}>Changes Needed</span>
          )}
        </div>
        {isApproved ? (
          <span className={styles.approved}>Approved</span>
        ) : (
          <span className={filesUploaded ? styles.approved : styles.disabled}>
            File {filesUploaded ? '✓' : '✗'}
          </span>
        )}
      </div>
      {isApproved ? (
        <div className={styles.tags}>
          <a
            href={item?.url || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.tag}
          >
            {item?.name || item?.key}
          </a>
        </div>
      ) : (
        <>
          <div className={styles.stats}>
            <div className={styles.statContent}>
              <div className={styles.statItem}>
                <Text>Content Due Date:</Text>
              </div>
              <Text>
                {getDateLabelFromDeliverable(
                  'content',
                  deliverable as
                    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
                    | undefined
                )}
              </Text>
            </div>
            <div className={styles.divider} />
            <div className={styles.statContent}>
              <div className={styles.statItem}>
                <Text>Content Type:</Text>
              </div>
              <Text>{startCase(mediaType?.toLowerCase() ?? '')}</Text>
            </div>
          </div>
          <DropdownItem
            label="Description"
            labelClassName={styles.descriptionLabel}
            className={styles.descriptionContainer}
            defaultExpanded
          >
            {item?.task?.description}
          </DropdownItem>
          <input
            type="hidden"
            name={`content.${formIndex}.deliverableItemId`}
            ref={methods.register}
            value={item?.id}
          />
          <UploadTimelineFilesInput
            fieldName={`content.${formIndex}.files`}
            inputTitle={'Drop files here or click to upload'}
            inputDescription={'Support for images and videos up to 5gb'}
            tagIcon={isImage ? 'image' : 'video'}
            acceptFileTypes={
              isImage
                ? { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }
                : AWS_MEDIACONVERT_VIDEO_FORMATS
            }
          />
        </>
      )}
    </div>
  );
};

export default SubmitContent;
